import { useState } from "react";
import { Controller, useForm } from "react-hook-form";

import { intl } from "LocaleWrapper";

import { Input, FormGroup, FileUpload, Select, showSnackbar } from "@cofano/cck";
import { useCoastguard } from "@cofano/coastguard2-frontend-client";

import { ExceptionErrorContent } from "components/utils/Request2";
import { FormDialog } from "components/dialogs/Dialogs";
import FormField from "components/cck/FormField";
import DatePicker from "components/cck/DatePicker";

import { Entity } from "./Documents";
import { DocumentEntity, DocumentType } from "__generated__/graphql";
import { useQuery } from "@apollo/client";
import { Alert } from "@mui/material";
import { buildDocumentDescription, getDocumentsCardTitle, uploadDocuments } from "components/documents/DocumentsUtils";

import { GetMaxFileSizeDocument } from "__generated__/view/admin/graphql/admin.queries.generated";

type Document = {
	files: File[],
	issuedAt: number;
	description: string;
	documentType: string;
	validUntil?: number;
	entity?: Entity;
};

type DocumentUploadDialogProps = {
	open: boolean;
	onClose: () => void;
	entities: Entity[];
	defaultEntity?: Entity;
	defaultDocumentType?: DocumentType;
	documentTypeOptions?: string[];
	onUploaded: () => void;
	warning?: string;
};

const DocumentUploadDialog = ({
	open,
	onClose,
	entities,
	defaultEntity,
	defaultDocumentType,
	documentTypeOptions,
	onUploaded,
	warning,
}: DocumentUploadDialogProps) => {

	const { instance } = useCoastguard();

	const [ loading, setLoading ] = useState(false);
	const { data: { maxFileSize } = { maxFileSize: 0 } } = useQuery(GetMaxFileSizeDocument);

	const defaultValues = {
		files: [],
		issuedAt: undefined,
		description: "",
		documentType: defaultDocumentType ?? DocumentType.Other,
		validUntil: undefined,
		entity: defaultEntity ?? entities[0],
	};

	const { control, handleSubmit, formState, reset, watch, setValue } = useForm<Document>({
		defaultValues: defaultValues,
	});

	const description = watch("description");
	const documentType = watch("documentType");

	const handleOnClose = () => {
		onClose();
		reset(defaultValues);
	};

	const onSave = (data: Document) => {

		const entity = data.entity || entities[0];

		setLoading(true);

		uploadDocuments(instance, data.files, [ entity ], {
			description: data.description,
			documentType: data.documentType as DocumentType,
			issuedAt: data.issuedAt,
			validUntil: data.validUntil,
		}).then(({ succeeded }) => {
			if (succeeded.length > 0) {
				handleOnClose();
				onUploaded();
			}
		}).catch(response => {
			showSnackbar(<ExceptionErrorContent response={response} />, { variant: "error" });
		}).finally(() => {
			setLoading(false);
		});
	};

	return (
		<FormDialog
			open={open}
			onClose={handleOnClose}
			title={getDocumentsCardTitle(entities)}
			form='documentForm'
			loading={loading}>
			<form
				id="documentForm"
				onSubmit={(event) => {
					event.stopPropagation();
					void handleSubmit(onSave)(event);
				}} >
				{warning && (
					<Alert severity="warning" sx={{ mb: 1 }}>{warning}</Alert>
				)}

				<FormGroup label="" overflow="hidden">
					<FormField name="files" errors={formState.errors}>
						<Controller
							name='files'
							control={control}
							rules={{ required: true }}
							render={({ field }) => (
								<FileUpload
									maxSize={maxFileSize}
									onChange={dropZoneFiles => {
										field.onChange(dropZoneFiles);
										if (!description) {
											setValue("description", buildDocumentDescription(dropZoneFiles));
										}
									}}
									onRejected={dropZoneFiles => {
										dropZoneFiles.forEach(item => {
											showSnackbar((
												<div>
													<p style={{ margin: 0, fontWeight: 600 }}>{item.file.name}</p>
													<p style={{ margin: 0 }}>{`${intl.formatMessage({ id: "FILE_TO_LARGE" }).replace("%d", (maxFileSize / (1024 * 1024)).toString())}. ${intl.formatMessage({ id: "CONTACT_ADMIN" })}`}</p>
												</div>
											), { variant: "error" });
										});
									}} />
							)} />
					</FormField>
				</FormGroup>

				<FormGroup label={intl.formatMessage({ id: "DESCRIPTION" })} overflow="hidden" required>
					<FormField name="description" errors={formState.errors}>
						<Controller
							name='description'
							control={control}
							rules={{ required: true }}
							render={({ field }) => <Input {...field} autoFocus />} />
					</FormField>
				</FormGroup>

				{documentTypeOptions && (
					<FormGroup label={intl.formatMessage({ id: "DOCUMENT_TYPE" })} overflow="hidden">
						<Controller
							name='documentType'
							control={control}
							render={({ field }) => (
								<Select
									{...field}
									fetchOptions={documentTypeOptions}
									getOptionLabel={option => intl.formatMessage({ id: option + "_DOCUMENT" })}
									clearable={false}
									disableSearch />
							)} />
					</FormGroup>
				)}

				{documentType === "SDS" && (
					<FormGroup label={intl.formatMessage({ id: "ISSUED_AT" })} overflow="hidden" required>
						<FormField name="issuedAt" errors={formState.errors}>
							<Controller
								rules={{ required: true }}
								name="issuedAt"
								control={control}
								render={({ field }) => <DatePicker {...field} />}
							/>
						</FormField>
					</FormGroup>
				)}

				{entities.length > 1 && (
					<FormGroup label={intl.formatMessage({ id: "ENTITY" })} overflow="hidden">
						<Controller
							name='entity'
							control={control}
							render={({ field }) => (
								<Select
									{...field}
									fetchOptions={entities as Entity[]}
									groupBy={(option: Entity) => intl.formatMessage({ id:  option.type })}
									clearable={false} />
							)} />
					</FormGroup>
				)}

				{entities[0]?.type === DocumentEntity.Ship && (
					<FormGroup label={intl.formatMessage({ id: "VALID_UNTIL" })} overflow="hidden">
						<Controller
							name='validUntil'
							control={control}
							render={({ field }) => <DatePicker {...field} />} />
					</FormGroup>
				)}
			</form>
		</FormDialog>
	);
};

export default DocumentUploadDialog;
